import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip, Stack, IconButton, Tooltip, Card, CardMedia, CircularProgress, Link } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PersonIcon from '@mui/icons-material/Person';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PDFViewer from '../ChatWindow/PDFViewer';
import FileViewer from '../ChatWindow/FileViewer';
import { supabase } from '../../lib/supabase';
import { DocumentService, DocumentInfo } from '../../services/documentService';
import { UserService } from '../../services/userService';
import { parseBoldTextInline } from '../../utils/textParser';

type ApplicationViewerModalProps = {
  open: boolean;
  onClose: () => void;
  application: any | null;
  onStatusUpdate?: (applicationId: string, status: 'accepted' | 'rejected' | 'submitted' | 'review') => void;
};

type DocItem = {
  id: string;
  name: string;
  url: string;
  type: string; // mime or guessed
  source: 'database' | 'storage';
  category?: 'resume' | 'additional' | 'other';
  fileSize?: number;
  mimeType?: string;
};

function normalizeDocuments(raw: any): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch {}
    if (raw.includes(',')) return raw.split(',').map((s: string) => s.trim()).filter(Boolean);
    return [raw];
  }
  if (typeof raw === 'object') {
    const values = Object.values(raw).filter(Boolean) as string[];
    return values.length > 0 ? values : [];
  }
  return [];
}

const asStoragePaths = (raw: any): string[] => {
  const out = normalizeDocuments(raw)
    .map((s) => String(s || '').trim())
    .filter(Boolean);
  return out;
};

const isHttpUrl = (s: string) => /^https?:\/\//i.test(s);

const getSignedUrlForApplicationObject = async (pathOrUrl: string): Promise<string | null> => {
  const raw = String(pathOrUrl || '').trim();
  if (!raw) return null;
  if (isHttpUrl(raw)) return raw;
  try {
    const { data, error } = await supabase.storage
      .from('applications')
      .createSignedUrl(raw, 3600);
    if (error) return null;
    return data?.signedUrl ?? null;
  } catch {
    return null;
  }
};

const fileNameFromPath = (p: string) => {
  const last = String(p || '').split('/').pop() || 'document';
  return last.split('?')[0] || 'document';
};

const guessType = (urlOrName: string): string => {
  const lower = urlOrName.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.csv')) return 'text/csv';
  if (lower.match(/\.(xlsx|xls)$/)) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (lower.match(/\.(png|jpg|jpeg|webp)$/)) return 'image/*';
  if (lower.match(/\.(doc|docx)$/)) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  return 'application/octet-stream';
};

const ApplicationViewerModal: React.FC<ApplicationViewerModalProps> = ({ open, onClose, application, onStatusUpdate }) => {
  const [loading, setLoading] = React.useState(false);
  const [docs, setDocs] = React.useState<DocItem[]>([]);
  const [viewerOpen, setViewerOpen] = React.useState(false);
  const [viewerFile, setViewerFile] = React.useState<DocItem | null>(null);
  const [applicantProfile, setApplicantProfile] = React.useState<any>(null);
  const [loadingProfile, setLoadingProfile] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [updatingStatus, setUpdatingStatus] = React.useState(false);

  const fullName = application ? `${application?.applicant?.first_name || ''} ${application?.applicant?.last_name || ''}`.trim() : '';

  const openViewer = (doc: DocItem) => {
    setViewerFile(doc);
    setViewerOpen(true);
  };

  const handleDownload = async (doc: DocItem) => {
    try {
      const response = await fetch(doc.url);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch {}
  };

  const loadApplicantProfile = async (applicantId: string) => {
    setLoadingProfile(true);
    try {
      const profile = await UserService.getUserProfile(applicantId);
      setApplicantProfile(profile);
    } catch (error) {
      console.error('Error loading applicant profile:', error);
      setApplicantProfile(null);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleStatusUpdate = async (status: 'accepted' | 'rejected' | 'submitted' | 'review') => {
    if (!application?.id || !onStatusUpdate) return;
    
    setUpdatingStatus(true);
    try {
      await onStatusUpdate(application.id, status);
    } catch (error) {
      console.error('Error updating application status:', error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  React.useEffect(() => {
    if (!open || !application) return;
    setLoading(true);
    
    const loadDocuments = async () => {
      try {
        const merged: DocItem[] = [];
        const seen = new Set<string>();

        const pushDoc = (doc: DocItem) => {
          const key = `${doc.source}:${doc.id}`;
          if (seen.has(key)) return;
          if (!doc.url) return;
          seen.add(key);
          merged.push(doc);
        };

        // Check if documents is an array of UUIDs (new format)
        if (Array.isArray(application.documents) && application.documents.length > 0) {
          // Check if first element looks like a UUID (new format)
          const firstDoc = application.documents[0];
          if (typeof firstDoc === 'string' && firstDoc.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
            // New format: array of UUIDs
            console.log('Loading documents by UUIDs:', application.documents);
            const documentInfos = await DocumentService.getDocumentsByIds(application.documents);
            
            for (const doc of documentInfos) {
              pushDoc({
                id: doc.id,
                name: doc.file_name,
                url: doc.url || '',
                type: doc.file_type || guessType(doc.file_name),
                source: 'database' as const,
                category: (doc as any)?.document_type === 'cv' ? 'resume' : 'additional',
                fileSize: doc.file_size,
                mimeType: doc.file_type,
              });
            }
          }
        }
        
        // Fallback to old format: treat as URLs/paths
        console.log('Loading documents using legacy format');
        for (const entry of normalizeDocuments(application.documents).filter(Boolean)) {
          const name = fileNameFromPath(entry);
          pushDoc({
            id: entry,
            name,
            url: entry,
            type: guessType(entry),
            source: 'database' as const,
            category: /resume|cv/i.test(name) ? 'resume' : 'other',
          });
        }

        // Supabase applications bucket paths (resume_url + additional_documents_urls)
        const resumePaths = asStoragePaths((application as any)?.resume_url);
        const additionalPaths = asStoragePaths((application as any)?.additional_documents_urls);

        for (const p of resumePaths) {
          const signed = await getSignedUrlForApplicationObject(p);
          if (!signed) continue;
          pushDoc({
            id: p,
            name: fileNameFromPath(p),
            url: signed,
            type: guessType(p),
            source: 'storage' as const,
            category: 'resume',
          });
        }

        for (const p of additionalPaths) {
          const signed = await getSignedUrlForApplicationObject(p);
          if (!signed) continue;
          pushDoc({
            id: p,
            name: fileNameFromPath(p),
            url: signed,
            type: guessType(p),
            source: 'storage' as const,
            category: 'additional',
          });
        }

        setDocs(merged);
      } catch (error) {
        console.error('Error loading documents:', error);
        setDocs([]);
      } finally {
        setLoading(false);
      }
    };

    // Load documents and applicant profile in parallel
    loadDocuments();
    
    // Load applicant profile if we have applicant ID
    if (application?.applicant?.id) {
      loadApplicantProfile(application.applicant.id);
    }
  }, [open, application]);

  if (!application) return null;

  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={isFullscreen ? false : "md"} 
      fullWidth={!isFullscreen}
      fullScreen={isFullscreen}
      PaperProps={{
        sx: {
          borderRadius: isFullscreen ? 0 : 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
        color: 'white',
        borderBottom: '1px solid rgba(255,255,255,0.15)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight={800} sx={{ color: 'white' }}>{application?.product?.name || 'Application'}</Typography>
            <Chip label={(application?.status || 'submitted').toUpperCase()} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', borderColor: 'rgba(255,255,255,0.35)' }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              {new Date(application?.created_at).toLocaleString()}
            </Typography>
            <Tooltip title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
              <IconButton onClick={toggleFullscreen} sx={{ color: 'white' }}>
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton onClick={onClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3, background: 'rgba(255, 255, 255, 0.6)' }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Applicant</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {fullName && (
                <Typography variant="body1" fontWeight={600}>{fullName}</Typography>
              )}
              {application?.applicant?.alias && (
                <Chip size="small" label={`@${application.applicant.alias}`} />
              )}
            </Box>
          </Box>

          {/* Professional Background Section */}
          {applicantProfile && (applicantProfile.work_experience || applicantProfile.education) && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                Professional Background
              </Typography>
              
              <Stack spacing={2}>
                {applicantProfile.work_experience && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <WorkIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Work Experience
                      </Typography>
                    </Box>
                    <Card sx={{ 
                      p: 2, 
                      bgcolor: 'rgba(14, 165, 233, 0.05)', 
                      borderRadius: 2,
                      border: '1px solid rgba(14, 165, 233, 0.1)'
                    }}>
                      {parseBoldTextInline(applicantProfile.work_experience, 'body2', {
                        lineHeight: 1.5,
                        color: 'text.primary'
                      })}
                    </Card>
                  </Box>
                )}

                {applicantProfile.education && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <SchoolIcon sx={{ color: 'secondary.main', fontSize: 18 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                        Education
                      </Typography>
                    </Box>
                    <Card sx={{ 
                      p: 2, 
                      bgcolor: 'rgba(139, 92, 246, 0.05)', 
                      borderRadius: 2,
                      border: '1px solid rgba(139, 92, 246, 0.1)'
                    }}>
                      {parseBoldTextInline(applicantProfile.education, 'body2', {
                        lineHeight: 1.5,
                        color: 'text.primary'
                      })}
                    </Card>
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          {/* Contact Information Section */}
          {applicantProfile && (applicantProfile.location || applicantProfile.phone || applicantProfile.website || applicantProfile.social_media_links) && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                Contact Information
              </Typography>
              
              <Stack spacing={1.5}>
                {applicantProfile.location && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {applicantProfile.location}
                    </Typography>
                  </Box>
                )}

                {applicantProfile.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {applicantProfile.phone}
                    </Typography>
                  </Box>
                )}

                {applicantProfile.website && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LanguageIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                    <Link 
                      href={applicantProfile.website.startsWith('http') ? applicantProfile.website : `https://${applicantProfile.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      {applicantProfile.website}
                    </Link>
                  </Box>
                )}

                {applicantProfile.social_media_links && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinkedInIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                    {(applicantProfile.social_media_links.linkedin || applicantProfile.social_media_links.linkedin_url) && (
                      <Link 
                        href={applicantProfile.social_media_links.linkedin || applicantProfile.social_media_links.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          color: '#0077b5',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        LinkedIn Profile
                      </Link>
                    )}
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          {/* Bio Section */}
          {applicantProfile?.bio && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  About
                </Typography>
              </Box>
              <Card sx={{ 
                p: 2, 
                bgcolor: 'rgba(16, 185, 129, 0.05)', 
                borderRadius: 2,
                border: '1px solid rgba(16, 185, 129, 0.1)'
              }}>
                {parseBoldTextInline(applicantProfile.bio, 'body2', {
                  lineHeight: 1.5,
                  color: 'text.primary'
                })}
              </Card>
            </Box>
          )}

          {/* Application Letter Section */}
          {application?.letter && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                Application Letter
              </Typography>
              <Card sx={{ 
                p: 2, 
                bgcolor: 'rgba(255, 255, 255, 0.8)', 
                borderRadius: 2,
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}>
                {parseBoldTextInline(application.letter, 'body2', {
                  lineHeight: 1.5,
                  color: 'text.primary'
                })}
              </Card>
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Documents</Typography>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">Loading documents…</Typography>
              </Box>
            ) : docs.length === 0 ? (
              <Box sx={{ p: 2, borderRadius: 2, background: 'rgba(0,0,0,0.03)', border: '1px dashed rgba(0,0,0,0.1)' }}>
                <Typography variant="body2" color="text.secondary">No documents were submitted with this application.</Typography>
              </Box>
            ) : (
              (() => {
                const resumeDocs = docs.filter((d) => d.category === 'resume');
                const additionalDocs = docs.filter((d) => d.category === 'additional');
                const otherDocs = docs.filter((d) => !d.category || d.category === 'other');

                const renderGrid = (items: DocItem[]) => (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
                    {items.map((doc, idx) => {
                      const isImage = doc.type.startsWith('image');
                      const icon = doc.type === 'application/pdf' ? <PictureAsPdfIcon fontSize="small" />
                        : doc.type.includes('sheet') || doc.type.includes('excel') || doc.name.toLowerCase().endsWith('.csv') ? <DescriptionIcon fontSize="small" />
                        : isImage ? <ImageIcon fontSize="small" />
                        : <InsertDriveFileIcon fontSize="small" />;
                      const isResume = doc.category === 'resume';
                      return (
                        <Box key={`${doc.source}:${doc.id}:${idx}`}>
                          <Card sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '1px solid rgba(0,0,0,0.08)',
                            boxShadow: isResume ? '0 10px 22px rgba(2,132,199,0.10)' : 'none',
                          }}>
                            {isImage ? (
                              <CardMedia component="img" image={doc.url} alt={doc.name} sx={{ height: 120, objectFit: 'cover' }} />
                            ) : (
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 120,
                                background: isResume
                                  ? 'linear-gradient(135deg, rgba(2,132,199,0.10) 0%, rgba(2,132,199,0.04) 100%)'
                                  : 'rgba(0,0,0,0.03)',
                              }}>
                                {icon}
                              </Box>
                            )}
                            <Box sx={{ p: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" noWrap title={doc.name} sx={{ fontWeight: isResume ? 800 : 600, maxWidth: 220 }}>
                                  {doc.name}
                                </Typography>
                                {isResume ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                    Resume / CV
                                  </Typography>
                                ) : doc.category === 'additional' ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                    Additional document
                                  </Typography>
                                ) : null}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                <Tooltip title="Open">
                                  <IconButton size="small" onClick={() => openViewer(doc)}>
                                    <OpenInNewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download">
                                  <IconButton size="small" onClick={() => handleDownload(doc)}>
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Card>
                        </Box>
                      );
                    })}
                  </Box>
                );

                return (
                  <Stack spacing={2}>
                    {resumeDocs.length > 0 && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main' }}>
                            Resume / CV
                          </Typography>
                          <Chip size="small" label={`${resumeDocs.length}`} color="primary" />
                        </Box>
                        {renderGrid(resumeDocs)}
                      </Box>
                    )}

                    {(additionalDocs.length > 0 || otherDocs.length > 0) && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                            Additional Documents
                          </Typography>
                          <Chip size="small" label={`${additionalDocs.length + otherDocs.length}`} />
                        </Box>
                        {renderGrid([...additionalDocs, ...otherDocs])}
                      </Box>
                    )}
                  </Stack>
                );
              })()
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(0,0,0,0.1)',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {onStatusUpdate && (
            <>
              <Button 
                onClick={() => handleStatusUpdate('accepted')}
                variant="contained"
                disabled={updatingStatus || application?.status === 'accepted'}
                startIcon={<CheckCircleIcon />}
                sx={{ 
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  },
                  '&:disabled': {
                    background: 'rgba(16, 185, 129, 0.3)',
                    color: 'rgba(255, 255, 255, 0.6)'
                  }
                }}
              >
                Accept
              </Button>
              <Button 
                onClick={() => handleStatusUpdate('rejected')}
                variant="contained"
                disabled={updatingStatus || application?.status === 'rejected'}
                startIcon={<CancelIcon />}
                sx={{ 
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  },
                  '&:disabled': {
                    background: 'rgba(239, 68, 68, 0.3)',
                    color: 'rgba(255, 255, 255, 0.6)'
                  }
                }}
              >
                Reject
              </Button>
              <Button 
                onClick={() => handleStatusUpdate('submitted')}
                variant="outlined"
                disabled={updatingStatus || application?.status === 'submitted'}
                sx={{ 
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                  '&:hover': {
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    borderColor: '#2563eb'
                  },
                  '&:disabled': {
                    borderColor: 'rgba(59, 130, 246, 0.3)',
                    color: 'rgba(59, 130, 246, 0.6)'
                  }
                }}
              >
                Mark as Submitted
              </Button>
              <Button 
                onClick={() => handleStatusUpdate('review')}
                variant="outlined"
                disabled={updatingStatus || application?.status === 'review'}
                sx={{ 
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderColor: '#f59e0b',
                  color: '#f59e0b',
                  '&:hover': {
                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                    borderColor: '#d97706'
                  },
                  '&:disabled': {
                    borderColor: 'rgba(245, 158, 11, 0.3)',
                    color: 'rgba(245, 158, 11, 0.6)'
                  }
                }}
              >
                Mark as Under Review
              </Button>
            </>
          )}
        </Box>
        
        <Button 
          onClick={onClose} 
          variant="outlined" 
          sx={{ 
            borderRadius: 2, 
            px: 3, 
            py: 1, 
            fontWeight: 'bold', 
            textTransform: 'none' 
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {viewerOpen && viewerFile ? (
      viewerFile.type === 'application/pdf' ? (
        <PDFViewer
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          fileUrl={viewerFile.url}
          fileName={viewerFile.name}
        />
      ) : (viewerFile.type.includes('sheet') || viewerFile.type.includes('excel') || viewerFile.name.toLowerCase().endsWith('.csv')) ? (
        <FileViewer
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          fileUrl={viewerFile.url}
          fileName={viewerFile.name}
          fileType={viewerFile.type}
        />
      ) : null
    ) : null}
    </>
  );
};

export default ApplicationViewerModal;


